import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Card,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { TabulatorFull as Tabulator } from "tabulator-tables";
import Q8Breadcrumbs from "../components/Q8Breadcrumbs";
import Q8Button from "../components/Q8Button";
import Q8SnackBar from "../components/Q8SnackBar";
import {
  FileDownload as ExportIcon,
  FilterList as FilterIcon,
} from "@mui/icons-material";
import "tabulator-tables/dist/css/tabulator.min.css";
import "../styles/tabulator.css";
import * as XLSX from "xlsx";
import LogService from "../service/logService";

function LogAdmin() {
  const tableRef = useRef(null);
  const [table, setTable] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    title: "",
    type: "default",
  });
  const [filtersActive, setFiltersActive] = useState(false);
  const [detailDialog, setDetailDialog] = useState({
    open: false,
    log: null,
  });

  // Funzione di utilità per formattare la data
  const formatDate = (dateString) => {
    const date = new Date(dateString);

    // Formattazione del giorno e mese con zero iniziale
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();

    // Ora, minuti e secondi
    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const seconds = String(date.getSeconds()).padStart(2, "0");

    // AM/PM
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12;
    hours = hours ? hours : 12; // l'ora '0' dovrebbe essere '12'

    return `${day}/${month}/${year}, ${hours}:${minutes}:${seconds} ${ampm}`;
  };

  const showNotification = (message, type = "default", title = "") => {
    setSnackbar({ open: true, message, title, type });
  };

  const fetchData = async () => {
    try {
      const data = await LogService.getErrorLogs();
      if (table) {
        table.setData(data);
      }
    } catch (error) {
      console.error("Error fetching log data:", error);
      showNotification("Errore nel caricamento dei log", "error", "Errore");
    }
  };

  const handleDownloadExcel = () => {
    if (table) {
      const data = table.getData();
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(data);
      XLSX.utils.book_append_sheet(wb, ws, "Error Logs");
      XLSX.writeFile(wb, "error_logs.xlsx");
    }
  };

  const handleViewDetails = (row) => {
    setDetailDialog({ open: true, log: row.getData() });
  };

  useEffect(() => {
    if (tableRef.current && table) {
      const tableElement = table.element;

      table.getColumns().forEach((column) => {
        if (column.getDefinition().headerFilter) {
          column.setHeaderFilterValue("");
        }
      });

      if (filtersActive) {
        tableElement.classList.remove("hide-filters");
      } else {
        tableElement.classList.add("hide-filters");
      }
    }
  }, [filtersActive, table]);

  useEffect(() => {
    if (tableRef.current && !table) {
      const resizeTable = () => {
        if (tableRef.current) {
          const height = tableRef.current.parentElement.clientHeight;
          tableRef.current.style.height = `${height}px`;
          if (table) {
            table.redraw(true);
          }
        }
      };

      const resizeObserver = new ResizeObserver(() => {
        window.requestAnimationFrame(() => {
          resizeTable();
        });
      });

      if (tableRef.current) {
        resizeObserver.observe(tableRef.current.parentElement);
      }

      const columns = [
        {
          title: "ID",
          field: "id",
          width: 80,
          headerFilter: "input",
        },
        {
          title: "Data e Ora",
          field: "logged",
          sorter: "datetime",
          headerFilter: "input",
          formatter: function (cell) {
            return formatDate(cell.getValue());
          },
        },
        {
          title: "Livello",
          field: "level",
          width: 100,
          headerFilter: "input",
        },
        {
          title: "Messaggio",
          field: "message",
          headerFilter: "input",
          formatter: function (cell) {
            const value = cell.getValue();
            return value
              ? value.substring(0, 100) + (value.length > 100 ? "..." : "")
              : "";
          },
        },
        {
          title: "Logger",
          field: "logger",
          headerFilter: "input",
        },
        {
          title: "Machine",
          field: "machineName",
          headerFilter: "input",
        },
        {
          formatter: function (cell, formatterParams, onRendered) {
            return "<div class='action-buttons'><button class='view-button'>Dettagli</button></div>";
          },
          width: 100,
          hozAlign: "center",
          headerSort: false,
          cellClick: function (e, cell) {
            if (e.target.classList.contains("view-button")) {
              handleViewDetails(cell.getRow());
            }
          },
        },
      ];

      const newTable = new Tabulator(tableRef.current, {
        columns: columns,
        layout: "fitDataFill",
        pagination: "local",
        paginationSize: 25,
        paginationSizeSelector: [10, 25, 50, 100],
        movableColumns: true,
        height: "100%",
        placeholder: "Nessun dato disponibile",
        initialSort: [{ column: "logged", dir: "desc" }],
        rowHeight: 40,
        selectable: false,
        responsiveLayout: false,
        autoResize: false,
        headerFilterLiveFilter: true,
        headerFilterPlaceholder: "Filtra...",
      });

      setTable(newTable);

      return () => {
        if (table) {
          table.destroy();
        }
        if (resizeObserver) {
          resizeObserver.disconnect();
        }
      };
    }
  }, []);

  useEffect(() => {
    if (table) {
      fetchData();
    }
  }, [table]);

  return (
    <Box sx={{ height: "100vh", display: "flex", flexDirection: "column" }}>
      <Q8Breadcrumbs
        links={[{ label: "Log Admin", href: "/log-admin" }]}
        variant="primary"
      />

      <Box
        sx={{
          mt: 3,
          mb: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography variant="h4" sx={{ color: "primary.main" }}>
          Log Admin - Errori
        </Typography>

        <Box sx={{ display: "flex", gap: 2 }}>
          <Q8Button
            variant="secondary"
            label="Esporta Excel"
            icon={<ExportIcon />}
            onClick={handleDownloadExcel}
            size="small"
          />
          <Q8Button
            variant="tertiary"
            label={filtersActive ? "Nascondi Filtri" : "Mostra Filtri"}
            icon={<FilterIcon />}
            onClick={() => {
              if (table) {
                const newState = !filtersActive;
                setFiltersActive(newState);
              }
            }}
            size="small"
          />
        </Box>
      </Box>

      <Card sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
        <div ref={tableRef} style={{ flex: 1 }}></div>
      </Card>

      <Dialog
        open={detailDialog.open}
        onClose={() => setDetailDialog({ ...detailDialog, open: false })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Dettagli Log</DialogTitle>
        <DialogContent>
          {detailDialog.log && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Box>
                <Typography variant="subtitle1" fontWeight="bold">
                  ID
                </Typography>
                <Typography>{detailDialog.log.id}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle1" fontWeight="bold">
                  Data e Ora
                </Typography>
                <Typography>{formatDate(detailDialog.log.logged)}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle1" fontWeight="bold">
                  Livello
                </Typography>
                <Typography>{detailDialog.log.level}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle1" fontWeight="bold">
                  Messaggio
                </Typography>
                <Typography
                  component="div"
                  sx={{
                    whiteSpace: "pre-wrap",
                    overflowWrap: "break-word",
                    maxHeight: "200px",
                    overflowY: "auto",
                    padding: "8px",
                    backgroundColor: "#f5f5f5",
                    borderRadius: "4px",
                  }}
                >
                  {detailDialog.log.message}
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle1" fontWeight="bold">
                  Eccezione
                </Typography>
                <Typography
                  component="div"
                  sx={{
                    whiteSpace: "pre-wrap",
                    overflowWrap: "break-word",
                    maxHeight: "200px",
                    overflowY: "auto",
                    padding: "8px",
                    backgroundColor: "#f5f5f5",
                    borderRadius: "4px",
                    color: "#d32f2f",
                  }}
                >
                  {detailDialog.log.exception || "N/A"}
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle1" fontWeight="bold">
                  Callsite
                </Typography>
                <Typography
                  component="div"
                  sx={{
                    whiteSpace: "pre-wrap",
                    overflowWrap: "break-word",
                    maxHeight: "150px",
                    overflowY: "auto",
                    padding: "8px",
                    backgroundColor: "#f5f5f5",
                    borderRadius: "4px",
                    fontFamily: "monospace",
                  }}
                >
                  {detailDialog.log.callsite || "N/A"}
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle1" fontWeight="bold">
                  Properties
                </Typography>
                <Typography
                  component="div"
                  sx={{
                    whiteSpace: "pre-wrap",
                    overflowWrap: "break-word",
                    maxHeight: "150px",
                    overflowY: "auto",
                    padding: "8px",
                    backgroundColor: "#f5f5f5",
                    borderRadius: "4px",
                  }}
                >
                  {detailDialog.log.properties || "N/A"}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Q8Button
            variant="primary"
            label="Chiudi"
            onClick={() => setDetailDialog({ ...detailDialog, open: false })}
          />
        </DialogActions>
      </Dialog>

      <Q8SnackBar
        open={snackbar.open}
        type={snackbar.type}
        title={snackbar.title}
        message={snackbar.message}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      />
    </Box>
  );
}

export default LogAdmin;
